import { AuthRoutes, PrivateRoute } from '@codezo/auth';
import { AuthRoleEnum } from '@codezo/graphql';
import { Navigate, Route, BrowserRouter as Router, Routes, useParams } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { useAuth } from './hooks/useAuth';
import { formatLoginUser } from './lib/format-auth-user';
import { PlatformLayout } from './layouts/PlatformLayout';
import { BrandDetailV2Page } from './pages/brands/BrandDetailV2Page';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { GuestAcceptPage } from './pages/guest/GuestAcceptPage';
import { GuestGate } from './pages/guest/GuestGate';
import { GuestOrderPage } from './pages/guest/GuestOrderPage';
import { GuestPreviewPage } from './pages/guest/GuestPreviewPage';
import { GuestQuotePage } from './pages/guest/GuestQuotePage';
import { getGuestSession, guestOrderPath, guestQuotePath } from './lib/guest-session';
import { takeNotificationDestination } from './lib/notification-destination';
import { NotificationLinkRoute } from './pages/notifications/NotificationLinkRoute';
import { OrderDetailPageV2 } from './pages/orders/v2/OrderDetailPageV2';
import { OrdersPage } from './pages/orders/OrdersPage';
import { ProductDetailPage } from './pages/products/ProductDetailPage';
import { ProductsPage } from './pages/products/ProductsPage';
import { ProfilePage } from './pages/profile/ProfilePage';
import { QuoteDetailPage } from './pages/quotes/QuoteDetailPage';
import { QuotesPage } from './pages/quotes/QuotesPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { UsersPage } from './pages/users/UsersPage';
import './styles.css';

/**
 * A guest whose session can't be restored has no account to sign back into, so
 * the public login page is a dead end. Send them to their own quote instead —
 * that route re-runs the code challenge in place. Non-guest tabs get the
 * default behaviour.
 */
function resolveGuestFailureRedirect(): string | null {
	const session = getGuestSession();
	if (!session) return null;
	const latestOrderId = session.orderIds?.[session.orderIds.length - 1];
	return latestOrderId ? guestOrderPath(latestOrderId) : guestQuotePath(session.orderQuoteId);
}

/** An old /community/:id link: the product now opens on the normal product page. */
function CommunityProductRedirect() {
	const { id } = useParams<{ id: string }>();
	return <Navigate to={`/products/${id}`} replace />;
}

export default function App() {
	const { isAuthenticated, user, setUser, setAccessToken, token } = useAuth();

	return (
		<>
			<Toaster position="bottom-right" />
			<Router>
				<AuthRoutes
					setUser={setUser}
					setAccessToken={setAccessToken}
					userFormatter={formatLoginUser}
					token={token}
					requiredRole={AuthRoleEnum.Organization}
					resolveFailureRedirect={resolveGuestFailureRedirect}
					resolvePendingRedirect={takeNotificationDestination}
				/>
				<Routes>
					{/*
					 * Guest acceptance links. /guest-accept is public by necessity — the
					 * visitor has no account yet and the emailed code is the gate. The
					 * quote view behind it needs the same session as any client, but is
					 * kept outside PlatformLayout so a guest sees only their offer.
					 */}
					<Route path="/guest-accept" element={<GuestAcceptPage />} />
					{/*
					 * An admin looking at the guest view in a real guest session.
					 * A sibling of /guest-accept rather than a mode of it: there is
					 * no email to confirm and no code to enter, so the two share a
					 * destination and nothing else.
					 */}
					<Route path="/guest-preview" element={<GuestPreviewPage />} />
					<Route path="/guest/quotes/:id" element={<GuestQuotePage />} />
					<Route path="/guest/orders/:id" element={<GuestOrderPage />} />
					{/*
					 * Notification links. Every email and bell item points here rather
					 * than at a real route, so screens can move without breaking mail
					 * that was sent months ago. This is the one path that must never
					 * move — see NotificationLinkPage.
					 */}
					<Route path="/n/:type/:id" element={<NotificationLinkRoute />} />
					<Route
						path="/"
						element={
							<PrivateRoute
								isAuthenticated={isAuthenticated}
								user={user}
								requiredRole={AuthRoleEnum.Organization}
							>
								<GuestGate>
									<PlatformLayout />
								</GuestGate>
							</PrivateRoute>
						}
					>
						<Route index element={<Navigate to="/dashboard" replace />} />
						<Route path="dashboard" element={<DashboardPage />} />
						<Route path="products" element={<ProductsPage />} />
						<Route path="products/:id" element={<ProductDetailPage />} />
						{/* Community used to be its own page; it is a tab of Products now. Links
						    handed out to the old pages land on their new home. */}
						<Route
							path="community"
							element={<Navigate to="/products?tab=community" replace />}
						/>
						<Route path="community/:id" element={<CommunityProductRedirect />} />
						<Route path="profile" element={<ProfilePage />} />
						{/* The brands list this page absorbed. The kits are a section
						    of the profile now, so the old nav destination lands there
						    rather than 404ing. */}
						<Route path="brands" element={<Navigate to="/profile#brands" replace />} />
						<Route path="brands/:id" element={<BrandDetailV2Page />} />
						{/* The old page is gone; links handed out to it land on the current one. */}
						<Route
							path="brands/:id/v1"
							element={<Navigate to=".." relative="path" replace />}
						/>
						{/* The old preview URL. Links to it were handed out while v2 sat
						    alongside v1, so it keeps resolving rather than 404ing — and
						    lands on v2, which is what it always pointed at. */}
						<Route
							path="brands/:id/v2"
							element={<Navigate to=".." relative="path" replace />}
						/>
						<Route path="quotes" element={<QuotesPage />} />
						<Route path="quotes/:id" element={<QuoteDetailPage />} />
						{/* Both old drawings' URLs. Links to them were handed out while
						    the two designs sat side by side, so they keep resolving
						    rather than 404ing — and land on the page, which is the only
						    one now. */}
						<Route
							path="quotes/:id/v1"
							element={<Navigate to=".." relative="path" replace />}
						/>
						<Route
							path="quotes/:id/v2"
							element={<Navigate to=".." relative="path" replace />}
						/>
						<Route path="orders" element={<OrdersPage />} />
						<Route path="orders/:id" element={<OrderDetailPageV2 />} />
						{/* The old page is gone; links handed out to it land on the current one. */}
						<Route
							path="orders/:id/v1"
							element={<Navigate to=".." relative="path" replace />}
						/>
						{/* The old preview URL. Links to it were handed out while v2 sat
						    alongside v1, so it keeps resolving rather than 404ing — and
						    lands on v2, which is what it always pointed at. */}
						<Route
							path="orders/:id/v2"
							element={<Navigate to=".." relative="path" replace />}
						/>
						<Route path="users" element={<UsersPage />} />
						<Route path="settings" element={<SettingsPage />} />
					</Route>
				</Routes>
			</Router>
		</>
	);
}
